import { useState, useEffect } from 'react';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface GalleryItem {
  id: string;
  title: string;
  description?: string;
  image_url: string;
  category: string;
  order_index: number;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

interface GalleryCategory {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  order_index?: number;
  is_active?: number;
}
// ✅ permission helper
const hasPermFor = (id: string) => {
  try {
    const raw =
      localStorage.getItem('adminPermissions') ||
      localStorage.getItem('permissions') ||
      '[]';
    const perms = JSON.parse(raw);
    return perms.some((p: any) => p.id === id && p.enabled);
  } catch {
    return true; // default allow
  }
};

const GalleryManager = () => {
  const [images, setImages] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingImage, setEditingImage] = useState<GalleryItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const loadCategories = async () => {
    try {
      const list = await apiClient.getGalleryCategories();
      // API may return strings for fallback; normalize to objects
      const normalized: GalleryCategory[] = Array.isArray(list)
        ? list.map((c: any) => (typeof c === 'string' ? { id: c, name: c } : c))
        : [];
      setCategories(normalized);
    } catch (err) {
      // ignore for now
    }
  };

  useEffect(() => {
    loadGalleryItems();
  }, []);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadGalleryItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getGalleryItems();
      setImages(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load gallery items');
      console.error('Error loading gallery items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditImage = (image: GalleryItem) => {
    setEditingImage({ ...image });
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleAddImage = () => {
    const newImage: GalleryItem = {
      id: '',
      title: '',
      description: '',
      image_url: '',
      category: 'Hair Cuts',
      order_index: images.length + 1,
      is_active: 1
    };
    setEditingImage(newImage);
    setSelectedFile(null);
    setShowModal(true);
  };

  const handleSaveImage = async () => {
    if (!editingImage) return;

    try {
      setUploadingFile(true);
      const isEditing = images.find(img => img.id === editingImage.id);

      const formData = new FormData();
      formData.append('title', editingImage.title);
      formData.append('description', editingImage.description || '');
      formData.append('category', editingImage.category);
      formData.append('orderIndex', editingImage.order_index.toString());

      console.log('FormData preparation:', {
        title: editingImage.title,
        description: editingImage.description,
        category: editingImage.category,
        orderIndex: editingImage.order_index,
        hasSelectedFile: !!selectedFile,
        imageUrl: editingImage.image_url
      });

      // If a file was selected, append it using the field name expected by backend (multer.single('gallery'))
      if (selectedFile) {
        console.log('Appending file to FormData with field name "gallery"');
        formData.append('gallery', selectedFile);
      }
      // Otherwise, if there's an image URL (and no file), use that
      else if (editingImage.image_url && !editingImage.image_url.startsWith('data:')) {
        console.log('Appending imageUrl to FormData:', editingImage.image_url);
        formData.append('imageUrl', editingImage.image_url);
      }

      console.log('Final FormData entries:', Array.from(formData.entries()));

      if (isEditing) {
        console.log('Updating existing gallery item');
        await apiClient.updateGalleryItem(editingImage.id, formData);
      } else {
        console.log('Creating new gallery item');
        await apiClient.createGalleryItem(formData);
      }

      await loadGalleryItems();
      setShowModal(false);
      setEditingImage(null);
      setSelectedFile(null);
    } catch (error: any) {
      console.error('Failed to save gallery item:', error);
      setError(error?.message || 'Failed to save gallery item');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteImage = async (id: string) => {
    console.log('[GalleryManager] handleDeleteImage called with id:', id);
    if (confirm('Are you sure you want to delete this gallery item?')) {
      try {
        console.log('[GalleryManager] Confirmed deletion, calling API for:', id);
        await apiClient.deleteGalleryItem(id);
        await loadGalleryItems();
      } catch (error) {
        console.error('[GalleryManager] Failed to delete gallery item:', error);
        setError('Failed to delete gallery item');
        alert('Failed to delete gallery item'); // Added alert for visibility
      }
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    console.log('[GalleryManager] handleDeleteCategory called for:', id, name);
    const ok = window.confirm(`Delete category "${name}"?`);
    console.log('[GalleryManager] confirm returned:', ok);
    if (ok) {
      try {
        console.log('[GalleryManager] Confirmed category delete for:', id);
        await apiClient.deleteGalleryCategory(id);
        await loadCategories();
      } catch (err) {
        console.error('[GalleryManager] Failed to delete category:', err);
        alert('Failed to delete category');
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    console.log('Gallery file selected:', file);
    if (file && editingImage) {
      const MAX = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX) {
        setError('File too large. Max 5MB allowed.');
        return;
      }
      console.log('File size OK, setting selected file');
      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          console.log('File preview created');
          setEditingImage({
            ...editingImage,
            image_url: e.target.result as string
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const filteredImages = selectedCategory === 'All'
    ? images
    : images.filter(img => img.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
        <span className="ml-2 text-gray-600">Loading gallery...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadGalleryItems}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Gallery Management</h3>
        <button
          onClick={handleAddImage}
          className="bg-primary-400 hover:bg-primary-500 text-black px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center"
        >
          <i className="ri-add-line mr-2"></i>
          Add Image
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['All', ...categories.map(c => c.name)].map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${selectedCategory === category
              ? 'bg-primary-400 text-black'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            {category}
            {category !== 'All' && (
              <span className="ml-2 bg-white text-gray-600 px-2 py-1 rounded-full text-xs">
                {images.filter(img => img.category === category).length}
              </span>
            )}
          </button>
        ))}
        <button
          onClick={() => setCatModalOpen(true)}
          className="ml-auto px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Manage Categories
        </button>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredImages.map((image) => (
          <div key={image.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <div className="relative">
              <img
                src={resolveImageUrl(image.image_url)}
                alt={image.title}
                className="w-full h-48 object-cover"
                onError={(e) => {
                  console.error('Gallery image failed to load:', image.image_url);
                  e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                }}
                onLoad={() => {
                  console.log('Gallery image loaded successfully:', image.image_url);
                }}
              />
              <div className="absolute top-2 right-2 flex space-x-1">
                <span className={`px-2 py-1 text-xs rounded-full ${image.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                  {image.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">
                  {image.category}
                </span>
                <span className="text-xs text-gray-500">#{image.order_index}</span>
              </div>

              <h4 className="font-semibold text-gray-900 mb-1">{image.title}</h4>
              {image.description && (
                <p className="text-gray-600 text-sm line-clamp-2 mb-3">{image.description}</p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEditImage(image)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => handleDeleteImage(image.id)}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-image-line text-gray-400 text-4xl mb-4"></i>
          <p className="text-gray-600">No images found for this category.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && editingImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {images.find(img => img.id === editingImage.id) ? 'Edit Gallery Item' : 'Add Gallery Item'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Title</label>
                <input
                  type="text"
                  value={editingImage.title}
                  onChange={(e) => setEditingImage({ ...editingImage, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="Enter image title"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Description (Optional)</label>
                <textarea
                  value={editingImage.description || ''}
                  onChange={(e) => setEditingImage({ ...editingImage, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  rows={3}
                  placeholder="Enter image description"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Category</label>
                <select
                  value={editingImage.category}
                  onChange={(e) => setEditingImage({ ...editingImage, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Order</label>
                <input
                  type="number"
                  value={editingImage.order_index}
                  onChange={(e) => setEditingImage({ ...editingImage, order_index: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Upload Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <p className="text-sm text-gray-600 mt-1">or enter image URL below</p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
                <input
                  type="url"
                  value={editingImage.image_url}
                  onChange={(e) => setEditingImage({ ...editingImage, image_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="https://example.com/image.jpg"
                />
                {editingImage.image_url && (
                  <img
                    src={resolveImageUrl(editingImage.image_url)}
                    alt="Preview"
                    className="mt-2 w-full h-32 object-cover rounded-lg"
                    onError={(e) => {
                      console.error('Gallery preview image failed to load:', editingImage.image_url);
                      e.currentTarget.src = 'https://via.placeholder.com/400x200?text=Preview+Error';
                    }}
                    onLoad={() => {
                      console.log('Gallery preview image loaded successfully:', editingImage.image_url);
                    }}
                  />
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
                disabled={uploadingFile}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveImage}
                disabled={uploadingFile}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 flex items-center"
              >
                {uploadingFile ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Image'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category modal */}
      {catModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Gallery Categories</h3>
              <button onClick={() => setCatModalOpen(false)} className="text-gray-600 hover:text-gray-800">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-2 mb-4">
              <label className="block text-sm font-medium text-gray-700">New Category Name</label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                placeholder="e.g., Hair Cuts"
              />
              <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
              <input
                type="text"
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded"
                placeholder="Short description"
              />
              <button
                onClick={async () => {
                  if (!newCategory.name.trim()) return;
                  await apiClient.createGalleryCategory({ name: newCategory.name.trim(), description: newCategory.description });
                  setNewCategory({ name: '', description: '' });
                  await loadCategories();
                }}
                className="mt-2 px-4 py-2 bg-primary-400 text-black rounded font-semibold hover:bg-primary-500"
              >
                Add Category
              </button>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Existing Categories</h4>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                    <span>{cat.name}</span>
                    <button
                      onClick={() => handleDeleteCategory(cat.id, cat.name)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
